import { useCallback, useEffect, useRef, useState } from 'react';
import { getNleSceneName } from '../config/nleScenes';
import apiService from '../services/api.service';
import { collectGraphicPaths, discoverBlock, fetchBlock, sceneRecordPaths, stripFileName } from '../utils/sceneCaptions';
import { getStoredSession } from '../utils/sessionManager';

/**
 * OPUS-1619 — the caption data blocks for the scene this renderer is running.
 *
 * NOTHING IS HARDCODED HERE. Which graphics exist, their versions and their
 * caption names are all read at runtime:
 *
 *   selectedRenderer.captions[]          the graphics, with their versions
 *   <graphic>/<version>/<graphic>.json   names its captions in v_captions.refMap
 *   .../captions/<name>.json             the block itself
 *
 * FETCHED ON DEMAND, NOT AT LOAD. The event service names the layers each
 * event actually plays, so nothing is requested until an NLE event fires and
 * then only for those graphics. Each layer is fetched once and cached for the
 * session; concurrent events asking for the same layer share one request.
 *
 * The earlier version read every caption in the scene at page load. With
 * gfx_PermScore alone indexing 82 captions that was hundreds of requests
 * competing with the clock poll and the event timeline.
 *
 * NAMED CAPTIONS. Most graphics take their layer's default block. A builder
 * can instead name the exact caption a graphic binds: clock_start plays
 * gfx_FF_Formation twice and names homeFormation, then awayFormation. Those
 * are read from captions/<name>.json directly and answered under
 * `layer:caption`, so each side gets its own definition.
 *
 * PINNED SCENES. A profile listed in config/nleScenes (football_pls ->
 * PLS_2026_NLE) reads every block from that scene and nowhere else — not the
 * renderer's scene, and not a caption the operator used. When the renderer
 * runs that scene its own caption list is used; otherwise the scene is looked
 * up by name in scene-service once, on the first NLE event for that profile.
 * Its blocks are cached under `<scene>/<layer>`, apart from the renderer's.
 *
 * EVERY FAILURE IS A NO-OP: nothing fetched, or a bad response, simply leaves
 * that layer without a block, and its graphics keep the builder's data.
 */

export function useSceneDefinitions(renderer) {
  // REFS, not state: read at trigger() time, never painted. State here would
  // re-render TXLive as blocks arrive and re-memoise the event service
  // mid-match.
  // Keyed by layer for its default block, or `layer:caption` for a named one.
  const blocksRef = useRef({});     // key -> block, once resolved
  const missRef = useRef(new Set()); // keys known to have none, so we ask once
  const inFlightRef = useRef(new Map()); // key -> promise, to share a request
  const overrideRef = useRef(new Set()); // layers the operator's caption set
  const pathsRef = useRef(new Map());
  const sceneNameRef = useRef(null); // the scene this renderer runs
  const pinnedPathsRef = useRef(new Map()); // scene name -> promise of its paths
  const [status, setStatus] = useState('idle');

  // Paths only — no fetching. graphicPaths carries this renderer's scene_path
  // and each graphic's version, which is all that is needed to build a URL
  // later.
  useEffect(() => {
    pathsRef.current = collectGraphicPaths(renderer, process.env.REACT_APP_RENDERER_PREVIEW_URL);
    sceneNameRef.current = renderer?.scene?.name ?? renderer?.allocation_details?.scene?.name ?? null;
    // No fetching here — just say which graphics this renderer can serve,
    // across ALL its scenes. A layer missing from this list can never be
    // resolved, because the version is only knowable from a caption list
    // (there is no `latest`), and that is the first thing to check when a
    // graphic keeps the builder's data.
    const layers = [...pathsRef.current.keys()];
    console.log(`[SceneDefinitions] scene offers ${layers.length} graphic(s):`, layers);
  }, [renderer]);

  /**
   * A pinned scene's graphics. When the renderer runs that scene, its own
   * caption list already says everything, so scene-service is not asked: that
   * lookup needs the operator's token to be allowed to list the organisation's
   * scenes, which an operator's need not be.
   *
   * Otherwise the scene is looked up by name once. A scene that does not exist
   * is remembered as offering nothing, so its graphics keep the builder's
   * data. A failed lookup is not remembered: the next event retries.
   */
  const pinnedPaths = useCallback((name) => {
    if (sceneNameRef.current === name) return Promise.resolve(pathsRef.current);
    if (pinnedPathsRef.current.has(name)) return pinnedPathsRef.current.get(name);

    const p = apiService.getSceneByName(getStoredSession()?.orgId, name)
      .then((scene) => {
        if (!scene) {
          console.warn(`[SceneDefinitions] no scene named ${name} — its graphics keep the builder's data`);
          return new Map();
        }
        const paths = sceneRecordPaths(scene, process.env.REACT_APP_RENDERER_PREVIEW_URL);
        console.log(`[SceneDefinitions] ${name} offers ${paths.size} graphic(s):`, [...paths.keys()]);
        return paths;
      })
      .catch((err) => {
        pinnedPathsRef.current.delete(name);
        throw err;
      });

    pinnedPathsRef.current.set(name, p);
    return p;
  }, []);

  /**
   * Where a profile's blocks come from: its pinned scene, or the renderer's.
   * `prefix` keeps the two caches apart.
   */
  const sourceFor = useCallback((profileId) => {
    const name = getNleSceneName(profileId);
    if (!name) return { prefix: '', label: "this scene's presets", paths: async () => pathsRef.current };
    return { prefix: `${name}/`, label: name, paths: () => pinnedPaths(name) };
  }, [pinnedPaths]);

  /**
   * One block, loaded once and shared between concurrent callers. `load`
   * resolves to the block or null; null is remembered as a miss. A rejection
   * is NOT remembered: a network blip should not disarm a layer for the rest
   * of the match.
   */
  const remember = useCallback((key, load) => {
    if (blocksRef.current[key]) return Promise.resolve(blocksRef.current[key]);
    if (missRef.current.has(key)) return Promise.resolve(null);
    if (inFlightRef.current.has(key)) return inFlightRef.current.get(key);

    const p = Promise.resolve()
      .then(load)
      .then((block) => {
        inFlightRef.current.delete(key);
        if (block) {
          blocksRef.current = { ...blocksRef.current, [key]: block };
          return block;
        }
        missRef.current.add(key);
        return null;
      })
      .catch((err) => {
        inFlightRef.current.delete(key);
        console.warn(`[SceneDefinitions] ${key}: fetch failed`, err?.message || err);
        return null;
      });

    inFlightRef.current.set(key, p);
    return p;
  }, []);

  /** A layer's default block, found from its definition's caption list. */
  const blockFor = useCallback((source, layer) => remember(`${source.prefix}${layer}`, async () => {
    const path = (await source.paths()).get(layer);
    if (!path) {
      // The scene has no such graphic — a permanent answer, so it is
      // remembered rather than retried on every event.
      console.warn(`[SceneDefinitions] ${layer}: not in ${source.label}`);
      return null;
    }

    const hit = await discoverBlock(layer, path);
    if (hit?.block) {
      console.log(`[SceneDefinitions] ${source.prefix}${layer}: ${hit.via}`);
      return hit.block;
    }
    console.warn(
      `[SceneDefinitions] ${source.prefix}${layer}: no block (tried ${(hit?.tried || []).join(', ') || 'nothing'})` +
      " — this graphic keeps the builder's data",
    );
    return null;
  }), [remember]);

  /** Exactly the caption the builder named — never a different one. */
  const captionBlockFor = useCallback((source, layer, caption) => remember(`${source.prefix}${layer}:${caption}`, async () => {
    const dir = stripFileName((await source.paths()).get(layer));
    if (!dir) {
      console.warn(`[SceneDefinitions] ${layer}: not in ${source.label}`);
      return null;
    }

    const block = await fetchBlock(layer, dir, caption);
    if (block) {
      console.log(`[SceneDefinitions] ${source.prefix}${layer}: captions/${caption}.json (named by the event)`);
      return block;
    }
    console.warn(`[SceneDefinitions] ${source.prefix}${layer}: no captions/${caption}.json — this graphic keeps the builder's data`);
    return null;
  }), [remember]);

  /**
   * Called by the event service with what THIS event plays: `layers` for
   * their default blocks, `captions` ([{ layer, caption }]) for the graphics
   * that name theirs. The profile picks the scene they are read from. Returns
   * the blocks it has — keyed by layer, or by `layer:caption` — or null when it
   * has none, in which case every graphic keeps the builder's data.
   */
  const resolveSceneDefinition = useCallback(async (profileId, _eventKey, layers, captions) => {
    const source = sourceFor(profileId);
    const wanted = [
      ...(Array.isArray(layers) ? layers : [])
        .filter(Boolean)
        .map((layer) => [layer, () => blockFor(source, layer)]),
      ...(Array.isArray(captions) ? captions : [])
        .filter((c) => c?.layer && c?.caption)
        .map(({ layer, caption }) => [`${layer}:${caption}`, () => captionBlockFor(source, layer, caption)]),
    ];
    if (wanted.length === 0) return null;

    const found = await Promise.all(wanted.map(async ([key, get]) => [key, await get()]));
    const blocks = {};
    for (const [key, block] of found) if (block) blocks[key] = block;

    const n = Object.keys(blocks).length;
    setStatus(n > 0 ? 'ready' : 'empty');
    return n > 0 ? blocks : null;
  }, [sourceFor, blockFor, captionBlockFor]);

  /**
   * Every caption action the operator commits. THE COMMAND ITSELF names the
   * graphic and the caption — Maestro builds it as { layer, preset, caption }
   * — so when they work in a caption, that exact one becomes the block for
   * its layer, overriding whatever the default discovery found.
   *
   * Renderer-scene blocks only: a pinned profile's blocks are cached under
   * `<scene>/<layer>`, which this never writes.
   */
  const noteCaptionUse = useCallback((cmd) => {
    const layer = cmd?.layer ?? cmd?.caption?.id;
    const preset = cmd?.preset ?? cmd?.caption?.preset;
    if (!layer || !preset) return;

    const key = `${layer}:${preset}`;
    if (overrideRef.current.has(key)) return;
    overrideRef.current.add(key);

    const dir = stripFileName(pathsRef.current.get(layer));
    if (!dir) return;

    fetchBlock(layer, dir, preset)
      .then((block) => {
        if (!block) return;
        blocksRef.current = { ...blocksRef.current, [layer]: block };
        missRef.current.delete(layer);
        console.log(`[SceneDefinitions] ${layer}: using captions/${preset}.json (operator)`);
      })
      .catch(() => {});
  }, []);

  return { resolveSceneDefinition, noteCaptionUse, status };
}

export default useSceneDefinitions;
